import { Calendar, Eye, Clock, Edit, Trash2, CheckCircle, XCircle, BookOpen, User, TrendingUp, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import { cleanAuthorName, getAuthorInitial } from '@/lib/utils';
import type { Blog } from '@/lib/api/types';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

interface BlogListItemProps {
  blog: Blog;
  onEdit: () => void;
  onDelete: (blog: Blog) => void;
  onTogglePublish: (blogId: string, currentStatus: string) => void;
}

export function BlogListItem({ blog, onEdit, onDelete, onTogglePublish }: BlogListItemProps) {
  const handleTogglePublish = () => {
    onTogglePublish(blog.id, blog.status);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden hover:shadow-md transition-all duration-200 border-border/50 rounded-lg">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            {/* Cover Image */}
            <div className="w-full sm:w-48 h-48 sm:h-32 flex-shrink-0">
              {blog.coverImageUrl ? (
                <img
                  src={blog.coverImageUrl}
                  alt={blog.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-primary/40" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3">
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-foreground mb-2 line-clamp-2">
                    {blog.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {blog.excerpt || 'No excerpt available'}
                  </p>
                </div>

                <div className="sm:ml-4 flex items-center gap-2">
                  <Badge
                    variant={blog.status === 'published' ? 'default' : 'secondary'}
                    className={`text-xs font-medium ${blog.status === 'published'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      }`}
                  >
                    {blog.status === 'published' ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Published
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                  </Badge>
                </div>
              </div>

              {/* Meta Information */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span className="truncate max-w-[100px] sm:max-w-none">{cleanAuthorName(blog.authorName)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span className="whitespace-nowrap">{format(new Date(blog.createdAt), 'MMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {blog.viewCount} views
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {blog.status === 'published' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const blogUrl = blog.slug
                          ? `https://elimishacharity.org/blog/${blog.slug}`
                          : `https://elimishacharity.org/blog/${blog.id}`;
                        window.open(blogUrl, '_blank');
                      }}
                      className="h-7 px-3 text-xs"
                    >
                      <ExternalLink className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">View</span>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onEdit}
                    className="h-7 px-3 text-xs"
                  >
                    <Edit className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                  <PermissionGuard resource="blogs" action="publish">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTogglePublish}
                      className={`h-7 px-3 text-xs ${blog.status === 'published'
                          ? 'text-orange-600 hover:text-orange-700'
                          : 'text-green-600 hover:text-green-700'
                        }`}
                    >
                      {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                  </PermissionGuard>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(blog)}
                    className="h-7 px-3 text-xs text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
